// ── 홍보물 주문 데스크탑 테이블 ────────────────────────────────────────────────
import React from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import CourierInput from '../../../components/CourierInput';
import { WorkflowStepBadge, OrderedCheckbox, CompletedCheckbox } from './OrderItemComponents';
import { PROMO_HEADERS } from './utils';
import { isItemDone, resolvePromoRowState } from './promoRowState';
import { useIsDark } from '../../../hooks/useDarkMode';
import { PromoCompanyAssign } from './PromoCompanyAssign';
import {
  tableStyle, theadRowStyle, thStyle, tbodyRowStyle, tdStyle,
  editBtnStyle, deleteBtnStyle, resolvePromoRowColors,
} from './styles';

export function PromotionalTable({
  filteredOrders,
  shippingEdit, setShippingEdit,
  handleItemStepChange, handleItemOrderToggle, handleItemCancel,
  saveShippingEdit, openEdit, askDelete, openHistory,
}) {
  const isDark = useIsDark();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={theadRowStyle}>
            {PROMO_HEADERS.map(({ label, db }) => (
              <th key={label} style={thStyle}>
                <AdminColHeader label={label} db={db} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredOrders.map((o) => {
            // 행 상태(색/필터 단일 진실) — promoRowState.resolvePromoRowState
            const state = resolvePromoRowState(o);
            // 상태별 배경색 — isDark에 따라 불투명도 조정 (계약 관리와 동일 패턴)
            const c = resolvePromoRowColors(state === 'shipped', state === 'in_progress', isDark);
            return (
              // 왼쪽 4px 색 막대는 뺐다(2026-10-23 사용자: "날짜 왼쪽에 … 바 좀 없애줘") — 상태는 줄 배경색으로 본다
              <tr key={o.id} style={{ ...tbodyRowStyle, background: c.bg }}>
                {/* 접수일자 — 한 줄로 */}
                <td style={{ ...tdStyle, whiteSpace: 'nowrap', minWidth: 90 }}>{o.received_date || '-'}</td>
                <td style={tdStyle}>{o.category_name || '-'}</td>
                <td style={tdStyle}>{o.sales_person_name || '-'}</td>
                {/* 상호 + 발주담당자 칩 */}
                <td style={{ ...tdStyle, fontWeight: 700, maxWidth: 100 }}>
                  <div>{o.company_name || '-'}</div>
                  <PromoCompanyAssign order={o} />
                  {/* 발주담당자 칩 — 발주된 일반 아이템들의 ordered_by 집계 */}
                  {(() => {
                    const orderers = [...new Set(
                      (o.items || []).filter((i) => !i.no_shipping && i.is_ordered && i.ordered_by).map((i) => i.ordered_by)
                    )];
                    return orderers.length > 0
                      ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
                          {orderers.map((name) => (
                            <span key={name} style={{ fontSize: 10, background: 'var(--bg-subtle)', color: '#7c3aed', borderRadius: 5, padding: '1px 5px', fontWeight: 600 }}>{name}</span>
                          ))}
                        </div>
                      : null;
                  })()}
                </td>
                <td style={{ ...tdStyle, maxWidth: 160 }}>
                  {o.address || '-'}
                  {o.memo && <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-sub)', background: 'rgba(234,179,8,0.1)', borderRadius: 3, padding: '1px 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📝 {o.memo}</div>}
                </td>
                {/* 홍보물 컬럼 — 이름 + 수량 (배송불필요 아이템은 순차 단계 배지 유지) */}
                <td style={{ ...tdStyle, maxWidth: 220 }}>
                  {Array.isArray(o.items) && o.items.length > 0
                    ? o.items.map((i, k) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1, minHeight: 26, marginBottom: k < o.items.length - 1 ? 4 : 0, opacity: i.is_cancelled ? 0.5 : 1 }}>
                        {/* 배송불필요 아이템만 인라인 단계 배지 표시 */}
                        {i.no_shipping && Array.isArray(i.custom_steps) && i.custom_steps.length > 0
                          ? (i.workflow_step_id === i.custom_steps.length
                              ? <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(34,197,94,0.15)', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(34,197,94,0.4)', flexShrink: 0 }}>완료</span>
                              : <WorkflowStepBadge
                                  stepId={i.workflow_step_id}
                                  steps={i.custom_steps.map((s, si) => ({ id: si + 1, name: s.name, sort_order: si, is_active: true }))}
                                  onStepChange={(newId) => handleItemStepChange(o.id, i.id, newId)}
                                />
                            )
                          : null
                        }
                        {/* 이름 — 취소 시 취소선 */}
                        <span style={{ fontSize: 11, flex: 1, textDecoration: i.is_cancelled ? 'line-through' : 'none', color: i.is_cancelled ? 'var(--text-muted)' : undefined }}>{i.item_name}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', marginLeft: 2 }}>{i.quantity}개</span>
                        {/* 취소/복원 버튼
                            - 완료 상태(송장 입력 or 완료 체크)인 아이템은 취소 비활성화
                            - 이미 취소된 아이템의 복원 버튼은 항상 활성 */}
                        {(() => {
                          const done     = isItemDone(i);
                          const disabled = !i.is_cancelled && done;
                          return (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={(e) => { e.stopPropagation(); if (!disabled) handleItemCancel(o.id, i.id); }}
                              style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, border: '1px solid', flexShrink: 0, lineHeight: 1.4,
                                cursor: disabled ? 'default' : 'pointer',
                                background:  i.is_cancelled ? 'rgba(34,197,94,0.1)' : disabled ? 'var(--bg-subtle)' : 'var(--bg-card)',
                                borderColor: i.is_cancelled ? 'rgba(34,197,94,0.4)' : disabled ? 'var(--border)' : '#fca5a5',
                                color:       i.is_cancelled ? '#15803d' : disabled ? 'var(--border)' : '#dc2626',
                              }}
                            >
                              {i.is_cancelled ? '복원' : '취소'}
                            </button>
                          );
                        })()}
                      </div>
                    ))
                    : <span style={{ color: 'var(--text-muted)' }}>-</span>
                  }
                </td>
                {/* 발주/완료 컬럼
                    - 취소된 아이템: 아무것도 표시 안 함
                    - 배송불필요 + custom_steps 없음: 완료 체크박스 (is_ordered 기반)
                    - 배송불필요 + custom_steps 있음: 홍보물 컬럼 WorkflowStepBadge에서 처리하므로 빈칸
                    - 일반 + 택배정보 완비: 완료 배지
                    - 일반 + 택배정보 없음: 발주 체크박스 */}
                <td style={{ ...tdStyle, minWidth: 80 }}>
                  {Array.isArray(o.items) && o.items.length > 0
                    ? o.items.map((i, k) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', minHeight: 26, marginBottom: k < o.items.length - 1 ? 4 : 0 }}>
                        {i.is_cancelled
                          ? null   // 취소된 아이템은 숨김
                          : i.no_shipping
                            ? (Array.isArray(i.custom_steps) && i.custom_steps.length > 0
                                ? null  // custom_steps 있는 경우 WorkflowStepBadge가 처리
                                : <CompletedCheckbox
                                    isCompleted={i.is_ordered}
                                    completedBy={i.ordered_by}
                                    onToggle={() => handleItemOrderToggle(o.id, i.id, i.is_ordered)}
                                  />
                              )
                            : (i.courier?.trim() && i.tracking_number?.trim())
                              ? <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'rgba(34,197,94,0.15)', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(34,197,94,0.4)' }}>완료</span>
                              : <OrderedCheckbox
                                  isOrdered={i.is_ordered}
                                  orderedBy={i.ordered_by}
                                  onToggle={() => handleItemOrderToggle(o.id, i.id, i.is_ordered)}
                                />
                        }
                      </div>
                    ))
                    : null
                  }
                </td>
                {/* 택배정보 컬럼 — 클릭 시 팝오버 편집 */}
                <td style={{ ...tdStyle, minWidth: 160 }}>
                  {Array.isArray(o.items) && o.items.length > 0
                    ? o.items.map((i, k) => {
                      const isEditing = shippingEdit?.orderId === o.id && shippingEdit?.itemId === i.id;
                      return (
                        <div key={k} style={{ position: 'relative', display: 'flex', alignItems: 'center', minHeight: 26, marginBottom: k < o.items.length - 1 ? 4 : 0 }}>
                          {/* 항상 표시되는 트리거 */}
                          {i.no_shipping ? (
                            // 송장불필요 항목 — 뱃지만, 클릭 불가
                            <span style={{ fontSize: 10, fontWeight: 400, padding: '2px 7px', borderRadius: 10, background: 'var(--border)', color: 'var(--text-muted)' }}>
                              송장불필요
                            </span>
                          ) : (
                            <div
                              onClick={() => setShippingEdit({ orderId: o.id, itemId: i.id, courier: i.courier || '', trackingNumber: i.tracking_number || '' })}
                              style={{ fontSize: 11, color: 'var(--text-sub)', cursor: 'pointer', borderRadius: 5, padding: '3px 6px', transition: 'background 0.12s', lineHeight: 1.5 }}
                              onMouseEnter={(e) => { if (!isEditing) e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              {(i.courier || i.tracking_number)
                                ? <><span style={{ color: '#7c3aed', fontWeight: 600 }}>{i.courier}</span>{i.courier && i.tracking_number && <span style={{ color: 'var(--border)', margin: '0 4px' }}>|</span>}<span>{i.tracking_number}</span></>
                                : <span style={{ color: 'var(--border)', fontSize: 11 }}>입력</span>
                              }
                            </div>
                          )}
                          {/* 팝오버 — 편집 중일 때만 표시 */}
                          {isEditing && (
                            <>
                              {/* 배경 클릭 시 닫힘 */}
                              <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setShippingEdit(null)} />
                              {/* 팝오버 카드 */}
                              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 201, background: 'var(--bg-card)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.07)', border: '1px solid var(--border)', padding: '14px 16px', minWidth: 230 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 12 }}>택배 정보</div>
                                {/* 택배사 */}
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>택배사</label>
                                  <CourierInput
                                    value={shippingEdit.courier}
                                    onChange={(v) => setShippingEdit((p) => ({ ...p, courier: v }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveShippingEdit(); if (e.key === 'Escape') setShippingEdit(null); }}
                                    placeholder="예: CJ대한통운"
                                  />
                                </div>
                                {/* 송장번호 — 팝오버 오픈 시 기본 포커스 */}
                                <div style={{ marginBottom: 12 }}>
                                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>송장번호</label>
                                  <input
                                    autoFocus
                                    value={shippingEdit.trackingNumber}
                                    onChange={(e) => setShippingEdit((p) => ({ ...p, trackingNumber: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveShippingEdit(); if (e.key === 'Escape') setShippingEdit(null); }}
                                    placeholder="송장번호 입력"
                                    style={{ width: '100%', padding: '6px 9px', fontSize: 12, border: '1.5px solid var(--border)', borderRadius: 6, outline: 'none', boxSizing: 'border-box', transition: 'border 0.15s' }}
                                    onFocus={(e) => e.target.style.borderColor = '#a78bfa'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                                  />
                                </div>
                                {/* 버튼 */}
                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                  <button onClick={() => setShippingEdit(null)} style={{ fontSize: 12, padding: '5px 12px', background: 'var(--bg-subtle)', color: 'var(--text-muted)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>취소</button>
                                  <button onClick={saveShippingEdit} style={{ fontSize: 12, padding: '5px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>저장</button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                    : <span style={{ color: 'var(--text-muted)' }}>-</span>
                  }
                </td>
                {/* 액션 — 이력 + 수정 + 삭제 (한 줄) */}
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <button style={editBtnStyle} onClick={() => openEdit(o)}>수정</button>
                    <button style={deleteBtnStyle} onClick={() => askDelete(o)}>삭제</button>
                    <button style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: '#7c3aed', cursor: 'pointer' }} onClick={() => openHistory(o)}>이력</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
